import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';

import { TranslatePipe } from '@ngx-translate/core';
import { Team } from '../../models/team';
import { Skill, SkillAdapter } from '../../models/skill';
import { Role } from '../../models/role';
import { SkillsService } from '../../services/skills.service';
import { RolesService } from '../../services/roles.service';
import { TeamType } from '../../_utils/teamType';
import { ITeamRoleRequirement } from '../../_interfaces/teamRoleRequirement';
import { forkJoin, map } from 'rxjs';
import { PagedResponse } from '../../models/paged-response.model';
import { adaptPagedResponse } from '../../operators/paged-response-operatros';



@Component({
  selector: 'app-team-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslatePipe
],
  templateUrl: './team-form.component.html',
  styleUrl: './team-form.component.scss'
})
export class TeamFormComponent implements OnInit{
  // --- INPUTS & OUTPUTS ---
  @Input() teamToEdit: Team | null = null;
  @Output() formSubmitted = new EventEmitter<Team>();
  @Output() cancel = new EventEmitter<void>();

  // --- PROPERTIES ---
  teamForm!: FormGroup;
  skills: Skill[] = [];
  roles: Role[] = [];
  teamTypeList = Object.values(TeamType);
  isEditMode = false;
  formReady = false;

  constructor(
    private fb: FormBuilder,
    private skillsService: SkillsService,
    private skillAdapter: SkillAdapter,
    private rolesService: RolesService
  ) { }

  // --- LIFECYCLE HOOKS ---
  ngOnInit(): void {
    this.isEditMode = !!this.teamToEdit;
    this.initForm();

    const skills$ = this.skillsService.getSkills(1, 1000) // Assumer une grande page
        .pipe(
            // Application de l'opérateur pour adapter les items
            adaptPagedResponse<Skill, Skill>(this.skillAdapter),
            // Extraction du tableau d'items adapté
            map((response: PagedResponse<Skill>) => response.items)
        );
    
    const roles$ = this.rolesService.getRoles()

    // On attend que les deux appels soient terminés
    forkJoin({
      roles:roles$,
      skills: skills$
    }).subscribe(({ roles, skills }) => {
      this.roles = roles;
      this.skills = skills;

      // 1. On construit la structure des FormArrays
      this.buildRolesFormArray();
      this.buildSkillsFormArray();

      // 2. SEULEMENT APRES, si on est en mode édition, on peuple le formulaire
      if (this.isEditMode) {
        this.populateFormForEdit(this.teamToEdit!);
      }
      
      this.formReady = true;
    });
  }

  // --- GETTERS FOR FORM ARRAYS ---
  get skillsFormArray() {
    return this.teamForm.get('skills') as FormArray;
  }

  get rolesFormArray() {
    return this.teamForm.get('roles') as FormArray;
  }

  // --- FORM INITIALIZATION ---
  private initForm(): void {
    this.teamForm = this.fb.group({
      name: ['', Validators.required],
      description: ['', Validators.required],
      teamType: ['Undefined', Validators.required],
      skills: this.fb.array([]),
      roles: this.fb.array<FormGroup>([]),
    });
  }



  // --- FORM POPULATION ---
  private buildSkillsFormArray(): void {
    this.skillsFormArray.clear();
    this.skills.forEach(() => this.skillsFormArray.push(new FormControl(false)));
  }

  private buildRolesFormArray(): void {
    this.rolesFormArray.clear();
    this.roles.forEach(role => {
      this.rolesFormArray.push(this.fb.group({
        role: [role],
        selected: [false],
        required_fte: [null as number | null, [Validators.min(0.1)]]
      }));
    });
  }

  private populateFormForEdit(team: Team): void {
    this.teamForm.patchValue({
      name: team.name,
      description: team.description,
      teamType: team.teamType
    });

    // Populate skills
    this.skillsFormArray.clear();
    this.skills.forEach(skill => {
      const isSelected = team.skills.some(s => s.id === skill.id);
      this.skillsFormArray.push(new FormControl(isSelected));
    });

    // Populate roles
    this.rolesFormArray.clear();
    this.roles.forEach(availableRole => {
      const existingRoleRequirement = team.roles.find(trr => trr.role.id === availableRole.id);
      const isSelected = !!existingRoleRequirement;
      const requiredFte = existingRoleRequirement ? existingRoleRequirement.required_fte : null;

      const roleGroup = this.fb.group({
        role: [availableRole],
        selected: [isSelected],
        required_fte: [requiredFte, Validators.min(0.1)]
      });

      this.updateFteValidation(roleGroup.get('selected') as FormControl, roleGroup.get('required_fte') as FormControl);
      this.rolesFormArray.push(roleGroup);
    });
  }

  // --- FORM EVENT HANDLERS ---
  onRoleCheckboxChange(index: number): void {
    const roleGroup = this.rolesFormArray.at(index) as FormGroup;
    const selectedControl = roleGroup.get('selected') as FormControl;
    const fteControl = roleGroup.get('required_fte') as FormControl;

    if (selectedControl.value && (fteControl.value === null || fteControl.value === 0)) {
      fteControl.setValue(1); // Valeur par défaut si cochée sans valeur
    }

    this.updateFteValidation(selectedControl, fteControl);
  }
  
  private updateFteValidation(selectedControl: FormControl, fteControl: FormControl): void {
    if (selectedControl.value) {
      fteControl.setValidators([Validators.required, Validators.min(0.1)]);
    } else {
      fteControl.clearValidators();
      fteControl.setValue(null);
    }
    fteControl.updateValueAndValidity();
  }

  resetForm(): void {
    this.teamForm.reset({ teamType: 'Undefined' });
    this.buildSkillsFormArray();
    this.buildRolesFormArray();
  }

  // --- SUBMISSION & CANCELLATION ---
  onSubmit(): void {
    if (this.teamForm.invalid) {
      this.teamForm.markAllAsTouched();
      return;
    }

    const formValue = this.teamForm.value;
    const selectedSkills = this.skills.filter((_, index) => formValue.skills[index]);

    const selectedRoles: ITeamRoleRequirement[] = this.rolesFormArray.controls
      .filter(control => control.get('selected')?.value)
      .map(control => ({
        role: control.get('role')?.value as Role,
        required_fte: control.get('required_fte')?.value
      }));

    const submittedTeam = new Team(
      this.teamToEdit ? this.teamToEdit.id : 0, // 0 pour une nouvelle équipe
      formValue.name,
      formValue.description,
      new Date(),
      new Date(),
      selectedSkills,
      selectedRoles,
      formValue.teamType,
      this.teamToEdit ? this.teamToEdit.manager : null
    );

    this.formSubmitted.emit(submittedTeam);
  }

  onCancel(): void {
    this.cancel.emit();
  }
}